import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../../../../../shared/shared.api';
import {
  getApprovalReadiness,
  getWorkOrder,
  listWorkOrderTransitions,
  type WorkOrderApprovalReadiness,
  type WorkOrderTransitionView,
  type WorkOrderView,
} from '../work-orders.api';

export type WorkOrderDetailState =
  | { status: 'loading' }
  | { status: 'loaded'; order: WorkOrderView; transitions: WorkOrderTransitionView[]; readiness: WorkOrderApprovalReadiness | null }
  | { status: 'not_found' }
  | { status: 'forbidden' }
  | { status: 'error' };

function toErrorState(err: unknown): WorkOrderDetailState {
  if (err instanceof ApiError && err.status === 404) return { status: 'not_found' };
  return { status: err instanceof ApiError && err.status === 403 ? 'forbidden' : 'error' };
}

/** Whether Approve can be pressed only matters while the order is raised; a failed readiness read must not hide the order. */
async function readinessFor(order: WorkOrderView): Promise<WorkOrderApprovalReadiness | null> {
  if (order.state !== 'raised') return null;
  return getApprovalReadiness(order.id).catch(() => null);
}

async function fetchDetail(id: string): Promise<WorkOrderDetailState> {
  const [order, transitions] = await Promise.all([getWorkOrder(id), listWorkOrderTransitions(id)]);
  return { status: 'loaded', order, transitions, readiness: await readinessFor(order) };
}

/** One work order with its transitions and, while raised, whether it can be approved. `reload` re-reads all three after a move without going back to the skeleton. */
export function useWorkOrderDetail(id: string) {
  const [state, setState] = useState<WorkOrderDetailState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    fetchDetail(id)
      .then((next) => !cancelled && setState(next))
      .catch((err) => !cancelled && setState(toErrorState(err)));
    return () => {
      cancelled = true;
    };
  }, [id]);

  const reload = useCallback(async (): Promise<void> => {
    try {
      setState(await fetchDetail(id));
    } catch (err) {
      setState(toErrorState(err));
    }
  }, [id]);

  return { state, reload };
}
